import { useCallback, useEffect, useRef, useState } from "react";
import { Mic, Square } from "lucide-react";

// Design: components/VoiceInput.tsx. The design falls back to playing a sample
// recording when the mic or speech recognition isn't available; here it says
// so and stops, so nothing typed is ever made up.

export const BAR_COUNT = 28;
const quiet = () => Array.from({ length: BAR_COUNT }, () => 0.08);

/** Voice capture with a live level meter and live transcript (real mic + Web Speech). */
export function useVoiceInput({ onTranscript, lang = "en-IN" }) {
  const [status, setStatus] = useState("idle"); // idle | starting | listening | finishing
  const [levels, setLevels] = useState(quiet);
  const [seconds, setSeconds] = useState(0);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const mode = "live";

  const rec = useRef(null);
  const stream = useRef(null);
  const ctx = useRef(null);
  const raf = useRef(null);
  const timers = useRef([]);
  const heard = useRef("");
  const active = useRef(false);
  const cb = useRef(onTranscript);
  cb.current = onTranscript;

  const clearAll = useCallback(() => {
    active.current = false;
    if (raf.current) cancelAnimationFrame(raf.current);
    raf.current = null;
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
    try { rec.current?.abort(); } catch (_) { /* already stopped */ }
    rec.current = null;
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = null;
    ctx.current?.close().catch(() => undefined);
    ctx.current = null;
  }, []);

  useEffect(() => clearAll, [clearAll]);

  useEffect(() => {
    if (status !== "listening") return undefined;
    const t = window.setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(t);
  }, [status]);

  const finish = useCallback(() => {
    if (!active.current) return;
    clearAll();
    setStatus("finishing");
    setLevels(quiet());
    const t = window.setTimeout(() => setStatus("idle"), 450);
    timers.current.push(t);
    if (!heard.current.trim()) setError("Olli couldn't hear anything. Try again or type your instructions.");
  }, [clearAll]);

  /** The mic or speech service isn't available: say why and stop. */
  const unavailable = useCallback((why) => {
    setNotice(why);
    clearAll();
    setStatus("idle");
    setLevels(quiet());
  }, [clearAll]);

  const meterFromMic = (s) => {
    const Ctx = window.AudioContext ?? window.webkitAudioContext;
    if (!Ctx) return false;
    const audio = new Ctx();
    const analyser = audio.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.7;
    audio.createMediaStreamSource(s).connect(analyser);
    ctx.current = audio;
    const data = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      analyser.getByteFrequencyData(data);
      const next = [];
      const span = Math.floor((data.length * 0.6) / BAR_COUNT) || 1;
      for (let i = 0; i < BAR_COUNT; i++) {
        let sum = 0;
        for (let j = 0; j < span; j++) sum += data[i * span + j] ?? 0;
        next.push(Math.max(0.08, Math.min(1, (sum / span / 255) * 1.6)));
      }
      setLevels(next);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return true;
  };

  const start = useCallback(async () => {
    if (active.current) return;
    active.current = true;
    heard.current = "";
    setError("");
    setNotice("");
    setSeconds(0);
    setStatus("starting");
    const Ctor = window.SpeechRecognition ?? window.webkitSpeechRecognition;

    let micOk = false;
    try {
      if (!navigator.mediaDevices?.getUserMedia) throw new Error("no media");
      const s = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!active.current) { s.getTracks().forEach((t) => t.stop()); return; }
      stream.current = s;
      micOk = meterFromMic(s);
    } catch (_) {
      micOk = false;
    }
    if (!active.current) return;
    if (!micOk) { unavailable("The microphone is blocked. Allow it in your browser to speak, or type instead."); return; }
    if (!Ctor) { unavailable("This browser can't turn speech into text yet. Type instead, or try Chrome."); return; }
    setStatus("listening");

    try {
      const r = new Ctor();
      r.lang = lang;
      r.continuous = true;
      r.interimResults = true;
      let finalText = "";
      r.onresult = (e) => {
        let interim = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          const res = e.results[i];
          if (res.isFinal) finalText += `${res[0].transcript} `;
          else interim += res[0].transcript;
        }
        heard.current = `${finalText}${interim}`.trim();
        cb.current(heard.current);
      };
      r.onerror = (e) => {
        if (!active.current) return;
        if (e.error === "no-speech") return;
        if (e.error === "not-allowed" || e.error === "service-not-allowed" || e.error === "network" || e.error === "audio-capture") {
          rec.current = null;
          unavailable("The speech service isn't available here. Type instead.");
        } else {
          setError("Olli couldn't hear that. Try again or type your instructions.");
          finish();
        }
      };
      r.onend = () => { if (active.current && rec.current === r) finish(); };
      rec.current = r;
      r.start();
    } catch (_) {
      unavailable("The speech service isn't available here. Type instead.");
    }
  }, [finish, lang, unavailable]);

  const stop = useCallback(() => {
    if (!active.current) return;
    try { rec.current?.stop(); } catch (_) { /* ignore */ }
    finish();
  }, [finish]);

  return { status, levels, seconds, notice, error, mode, start, stop, clearError: () => setError("") };
}

const clock = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

/** Mic button with pulse rings while listening. */
export function VoiceButton({ status, onStart, onStop }) {
  const on = status === "listening" || status === "starting";
  return (
    <span className="relative inline-flex h-12 w-12 items-center justify-center">
      {on && (
        <>
          <span aria-hidden className="absolute inset-0 rounded-full bg-sky animate-voice-ring motion-reduce:hidden" />
          <span aria-hidden className="absolute inset-0 rounded-full bg-sky animate-voice-ring motion-reduce:hidden [animation-delay:800ms]" />
        </>
      )}
      <button
        type="button"
        onClick={on ? onStop : onStart}
        aria-label={on ? "Stop recording" : "Speak your instructions"}
        aria-pressed={on}
        className={`relative flex h-12 w-12 items-center justify-center rounded-full transition-colors duration-200 ${on ? "bg-ink text-paper" : "bg-paper text-ink ring-1 ring-line hover:bg-mist"}`}
      >
        {on ? <Square size={15} fill="currentColor" strokeWidth={1.75} /> : <Mic size={19} strokeWidth={1.75} />}
      </button>
    </span>
  );
}

/** Live level meter + state line. Sits on sky-soft so it reads as a recording strip. */
export function VoiceStrip({ status, levels, seconds, notice, onStop }) {
  if (status === "idle") return notice ? <p className="mt-2 px-2 text-caption text-muted">{notice}</p> : null;
  const label = status === "starting" ? "Starting microphone" : status === "finishing" ? "Got it" : "Listening";
  return (
    <div className={`mt-4 rounded-md bg-sky-soft px-4 py-3 ${status === "finishing" ? "animate-fade-in" : "animate-nav-in"}`} role="status" aria-live="polite">
      <div className="flex items-center gap-4">
        <span className="relative flex h-2.5 w-2.5 shrink-0">
          {status === "listening" && <span aria-hidden className="absolute inset-0 rounded-full bg-danger animate-voice-ring motion-reduce:hidden" />}
          <span className={`relative h-2.5 w-2.5 rounded-full ${status === "listening" ? "bg-danger" : "bg-sky-deep"}`} />
        </span>
        <span className="shrink-0 whitespace-nowrap text-small font-semibold">{label}</span>
        <div aria-hidden className="flex h-9 min-w-0 flex-1 items-center justify-center gap-1 overflow-hidden">
          {levels.map((lv, i) => (
            <span
              key={i}
              className="w-[3px] shrink-0 rounded-full bg-sky-deep transition-[height] duration-75 ease-out"
              style={{ height: `${Math.round(Math.max(3, lv * 36))}px`, opacity: 0.45 + lv * 0.55 }}
            />
          ))}
        </div>
        <span className="shrink-0 text-caption tabular-nums text-muted">{clock(seconds)}</span>
        {status === "listening" && (
          <button type="button" onClick={onStop} className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-paper px-3 text-caption font-semibold ring-1 ring-line hover:bg-mist">
            <Square size={11} fill="currentColor" strokeWidth={1.75} />
            Stop
          </button>
        )}
      </div>
      {notice && <p className="mt-2 text-caption text-muted">{notice}</p>}
    </div>
  );
}
